/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { AddSharp, CloudUpload, Search } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment, Menu,
  MenuItem,
  Snackbar,
  TextField
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  GetAllModelTypeArray,
  TransferOrderPartDetails,
} from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { isRoleDenied } from "../../../utils/permissions";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import ProductTableComponent from "../../Layouts/Table/ProductTableComponent";
import RoleGuard from "../../auth/RoleGuard";

const ProductTemplate = () => {
  const { id } = useParams();
  const canViewOem = !isRoleDenied([
    "ROLE_ERP_FRANCHISE_ADMIN",
    "ROLE_FRANCHISE_INVENTORY_MANAGER",
  ]);
  const canViewSalesPrice = !isRoleDenied([
    "ROLE_ERP_FRANCHISE_ADMIN",
    "ROLE_FRANCHISE_INVENTORY_MANAGER",
  ]);
  const columns = [
    "id",
    "description",
    "referenceCode",
    "rootPartReferenceCode",
    "leadTime",
    "productType",
    "hsn",
    "salesPrice",
    "costPrice",
    "quantity",
    "oemName",
    "status",
    // "autoPriceUpdate",
    "procurable",
    "isSpecialPart",
    "partTier",
    "partFamily",
    "partLocation",
  ];
  const columnHeaders = [
    "Product ID",
    "Product",
    "Reference Code",
    "Root Part Ref Code",
    "Lead Time",
    "Element Type",
    "HSN",
    "Sales Price",
    "Cost Price",
    "Quantity",
    "OEM Type",
    "Status",
    // "Price Update",
    "Procurable",
    "Special Part",
    "Part Tier",
    "Part Family",
    "Part Location",
  ];
  if (canViewOem) {
    columns.splice(4, 0, "oemName");
    columnHeaders.splice(4, 0, "OEM Type");
    columns.splice(9, 0, "oemName");
    columnHeaders.splice(9, 0, "OEM Type");
  }
  const visibleColumns = canViewSalesPrice
    ? columns
    : columns.filter((column) => column !== "salesPrice");
  const visibleColumnHeaders = canViewSalesPrice
    ? columnHeaders
    : columnHeaders.filter((header) => header !== "Sales Price");
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);
  const [selectedModelType, setSelectedModelType] =
    useState<GetAllModelTypeArray | null>(null);
  const [partTypes, setPartTypes] = useState<GetAllModelTypeArray[]>([]);
  const [selectedPartType, setSelectedPartType] =
    useState<GetAllModelTypeArray | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const { setLoading } = useLoading();

  const [parts, setParts] = useState<TransferOrderPartDetails[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<{
    id: number;
    status: string;
  } | null>(null);

  // Bulk upload state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  const [uploading, setUploading] = useState(false);

  // OEM Modal state
  const [oemModalOpen, setOemModalOpen] = useState(false);
  const [selectedOemList, setSelectedOemList] = useState<string[]>([]);
  const [selectedOemName, setSelectedOemName] = useState<string>("");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  const fetchPartTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getPartType();
      setPartTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching part types", error);
    }
  }, []);

  const handleBulkUploadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadSampleSheet = async () => {
    try {
      setLoading(true);
      await AllPartApi.api.downloadSampleSheet();
      setSnackbar({
        open: true,
        message: "Sample sheet downloaded successfully!",
        severity: "success",
      });
      handleMenuClose();
    } catch (error) {
      console.error("Error downloading sample sheet", error);
      setSnackbar({
        open: true,
        message: "Failed to download sample sheet.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    handleMenuClose();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setLoading(true);
      const response = await AllPartApi.api.uploadBulkParts(file);
      
      if (response.success) {
        // Success case - all records processed
        setSnackbar({
          open: true,
          message: response.message || "Parts uploaded successfully!",
          severity: "success",
        });
      } else if (response.hasErrorFile) {
        // Error case - error file was downloaded
        setSnackbar({
          open: true,
          message: response.message || "Some records could not be processed. Check the downloaded error file.",
          severity: "warning",
        });
      }
      
      // Refresh the parts list
      await fetchParts(true);
    } catch (error) {
      console.error("Error uploading parts", error);
      setSnackbar({
        open: true,
        message: "Failed to upload parts.",
        severity: "error",
      });
    } finally {
      setUploading(false);
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOemClick = (oemName: string, commonOemList: any) => {
    if (!canViewOem) return;

    if (Array.isArray(commonOemList) && commonOemList.length > 0) {
      setSelectedOemName(oemName);
      setSelectedOemList(commonOemList.filter((oem: any) => oem !== null));
      setOemModalOpen(true);
    }
  };

  const handleOemModalClose = () => {
    setOemModalOpen(false);
    setSelectedOemList([]);
    setSelectedOemName("");
  };

  const fetchParts = useCallback(
    async (resetPage = false) => {
      setLoading(true);
      try {
        const response = await AllPartApi.api.getPartList(
          resetPage ? 0 : page,
          rowsPerPage,
          searchQuery,
          undefined,
          undefined,
          selectedModelType?.id,
          undefined,
          undefined,
          undefined,
          true,
          selectedPartType?.id
        );
        setParts(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching parts", error);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchQuery, selectedModelType, selectedPartType]
  );

  useEffect(() => {
    fetchParts();
  }, [fetchParts]);

  useEffect(() => {
    if (canViewOem) {
      fetchModelTypes();
    }
  }, [fetchModelTypes]);

  useEffect(() => {
    fetchPartTypes();
  }, [fetchPartTypes]);

return (
  <div className="flex h-screen overflow-hidden">
    {/* Sidebar - Fixed width */}
    <div className="w-64 flex-shrink-0 bg-white shadow-md">
      <SideBarNavigation />
    </div>

    {/* Main content area - Takes remaining space */}
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <div className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F8FAFC] min-w-0">
        <div className="p-4 min-w-0">
          <h1 className="text-2xl font-semibold mb-3 text-left">
            Parts Details
          </h1>

            {/* Search and Add Button Bar */}
            <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <TextField
                  placeholder="Search by part name or ID"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  size="small"
                  sx={{
                    width: 230,
                    bgcolor: "white",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputBase-input": {
                      py: 0.75,
                      fontSize: "0.875rem",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                  <Autocomplete
                    options={modelTypes}
                    getOptionLabel={(option) => option.description}
                    value={selectedModelType}
                    onChange={(event, value) => {
                      setSelectedModelType(value);
                      setPage(0);
                    }}
                    size="small"
                    sx={{
                      width: 160,
                      bgcolor: "white",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3b82f6",
                        },
                      },
                      "& .MuiInputBase-input": {
                        py: 0.75,
                        fontSize: "0.875rem",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="OEM type"
                      />
                    )}
                  />
                </RoleGuard>
                <Autocomplete
                  options={partTypes}
                  getOptionLabel={(option) => option.description}
                  value={selectedPartType}
                  onChange={(event, value) => {
                    setSelectedPartType(value);
                    setPage(0);
                  }}
                  size="small"
                  sx={{
                    width: 160,
                    bgcolor: "white",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                      },
                    },
                    "& .MuiInputBase-input": {
                      py: 0.75,
                      fontSize: "0.875rem",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Element type"
                    />
                  )}
                />
              </div>
             <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="contained"
                  onClick={() => navigate("/part-family")}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#16A34A",
                    "&:hover": { bgcolor: "#15803D" },
                    fontSize: "14px",
                    borderRadius: "24px",
                    px: 2,
                    py: 0.5,
                    minHeight: 32,
                  }}>
                  Part Family
                </Button>
                <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={handleBulkUploadClick}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#4F46E5",
                    "&:hover": { bgcolor: "#4338CA" },
                    fontSize: "14px",
                    borderRadius: "24px",
                    px: 2,
                    py: 0.5,
                    minHeight: 32,
                  }}>
                  Bulk Upload
                </Button>
                </RoleGuard>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}>
                  <MenuItem onClick={handleDownloadSampleSheet}>
                    Download Sample Sheet
                  </MenuItem>
                  <MenuItem onClick={handleUploadClick}>Upload</MenuItem>
                </Menu>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
                <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Link to="/create/parts">
                  <Button
                    variant="contained"
                    startIcon={<AddSharp />}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#171F21",
                      "&:hover": { bgcolor: "#0077F" },
                      fontSize: "14px",
                      borderRadius: "24px",
                      px: 2,
                      py: 0.5,
                      minHeight: 32,
                    }}>
                    Add Order
                  </Button>
                </Link>
                </RoleGuard>
              </div>
            </div>
            {/* Table Container with horizontal scroll */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <ProductTableComponent
                columns={visibleColumns}
                columnHeaders={visibleColumnHeaders}
                data={parts}
                setSelectedOrder={setSelectedOrder}
                currentPage={page}
                setCurrentPage={setPage}
                totalPages={totalPages}
                onOemClick={handleOemClick}
              />
            </div>
          </div>

          </div>
        </div>
      </div>

      {/* OEM List Modal */}
      <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
        <Dialog
          open={oemModalOpen}
          onClose={handleOemModalClose}
          maxWidth="sm"
          fullWidth>
          <DialogTitle sx={{ fontWeight: "600" }}>
            {selectedOemName} - Available OEM Types
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            {selectedOemList.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectedOemList.map((oem, index) => (
                  <Chip
                    key={index}
                    label={oem}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            ) : (
              <p>No OEM types available</p>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOemModalClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </RoleGuard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductTemplate;
